import { useEffect, useState, useCallback, useRef } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router";
import { useUser } from "@clerk/react";
import { TopBar } from "../components/layout/TopBar";
import { Spinner } from "../components/shared/Spinner";
import { MarkdownToolbar } from "../components/editor/MarkdownToolbar";
import { api, type GitDocEntry } from "../api/client";
import {
  parseFrontMatter,
  stringifyFrontMatter,
  type FrontMatter,
} from "../lib/front-matter";
import { useAuditStore } from "../stores/useAuditStore";
import { useToastStore } from "../stores/useToastStore";
import { useGitStore } from "../stores/useGitStore";
import { useBatchStore } from "../stores/useBatchStore";
import { generateTemplateMarkdown, TEMPLATES } from "../lib/templates";
import {
  renderMarkdownPreview,
  previewElementCss,
} from "../lib/markdown-preview";
import type { SuggestionsLocationState } from "./SuggestionsPage";
import { formatApiError } from "../lib/api-errors";
import {
  docPath,
  isRepoPath,
  parseDocPath,
  slugify,
  websiteSlug,
} from "../../shared/doc-paths";
import { versionSortKey } from "../../shared/versions";
import {
  canPublish,
  canTransition,
  normalizeDocStatus,
  type DocStatus,
  type SanityTargetType,
} from "../../shared/types";
import { Button } from "../components/shared/Button";
import { SnippetSplitPanel } from "../components/editor/SnippetSplitPanel";

// Doc-type tags the editor can set. `value` is stored as `doc_type` front
// matter and passed as the PDF `template`, so picking one and clicking
// "generate pdf" previews that type's cover without going to review.
const DOC_TYPE_OPTIONS: ReadonlyArray<{
  value: SanityTargetType;
  label: string;
}> = [
  { value: "methodology", label: "Methodology" },
  { value: "factsheet", label: "Factsheet" },
  { value: "pdfDocument", label: "PDF" },
];

// Router state passed when an uploaded .docx is converted and opened as a new
// doc. Shared with the producer (DashboardPage) so the keys can't drift.
export interface ImportedDocLocationState {
  importedMarkdown: string;
  importedTitle: string;
  importedFamily: string;
}

export function DocumentEditorPage() {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isNewDoc = pageId === "new";
  const templateType = searchParams.get("template") ?? "";
  // The editor opens three kinds of documents: a fresh one ("new", optionally
  // from a template), a repo file (path-shaped id from FamilyPage), or a
  // Notion page (anything else).
  const repoPath = pageId && isRepoPath(pageId) ? pageId : null;

  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState<"pdf" | null>(null);
  const [fileSha, setFileSha] = useState<string | undefined>();
  const [draftPending, setDraftPending] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Metadata fields
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("v0.1");
  const [status, setStatus] = useState<DocStatus>("draft");
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [docType, setDocType] = useState("methodology");
  const [family, setFamily] = useState("");
  // Explicit public website slug (front-matter `slug`). Blank → derived from
  // the family at publish time. Lets a doc own its /static/ URL independently
  // of its git folder name.
  const [publicSlug, setPublicSlug] = useState("");

  const [showPreview, setShowPreview] = useState(true);
  const [supersedes, setSupersedes] = useState("");
  const [baselineVersion, setBaselineVersion] = useState("");
  const [baselineContent, setBaselineContent] = useState<string | null>(null);
  const [siblingVersions, setSiblingVersions] = useState<GitDocEntry[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  const { user } = useUser();
  const actorEmail = user?.primaryEmailAddress?.emailAddress;
  const addToast = useToastStore((s) => s.addToast);
  const invalidateGit = useGitStore((s) => s.invalidate);
  const fetchTree = useGitStore((s) => s.fetchTree);
  const batchId = useBatchStore((s) => s.batchId);
  const stageDoc = useBatchStore((s) => s.stage);
  const serverSnapshotRef = useRef<string>("");
  // Serialized "clean" editable state, captured when a load settles and after
  // each successful save. Auto-save/dirty fire only when current state differs.
  const cleanSnapshotRef = useRef<string | null>(null);
  // In PR mode the worker writes to a per-session branch; track it so reloads
  // and conflict-retry refetches read the in-flight version, not main.
  const prBranchRef = useRef<string | null>(null);

  // ── localStorage helpers ──
  interface DraftData {
    markdown: string;
    title: string;
    version: string;
    status: DocStatus;
    docType: string;
    family: string;
    effectiveDate: string;
    publicSlug: string;
  }

  const draftKey = pageId ? `shelf-draft:${pageId}` : null;

  const saveDraft = useCallback(
    (data: DraftData) => {
      if (!draftKey) return;
      try {
        localStorage.setItem(draftKey, JSON.stringify(data));
        setHasDraft(true);
      } catch {
        // localStorage may be full or unavailable; silently ignore
      }
    },
    [draftKey],
  );

  const loadDraft = useCallback((): DraftData | null => {
    if (!draftKey) return null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return null;
      return JSON.parse(raw) as DraftData;
    } catch {
      return null;
    }
  }, [draftKey]);

  const clearDraft = useCallback(() => {
    if (!draftKey) return;
    try {
      localStorage.removeItem(draftKey);
      setHasDraft(false);
    } catch {
      // silently ignore
    }
  }, [draftKey]);

  // Front-matter keys the UI doesn't edit (notion_last_edited_time,
  // content_hash, supersedes, …) — carried through so saving doesn't drop them.
  const extraFmRef = useRef<FrontMatter>({});

  const applyFrontMatter = useCallback((fm: FrontMatter, content: string) => {
    if (fm.title) setTitle(fm.title);
    else {
      const headingMatch = content.match(/^#\s+(.+)$/m);
      if (headingMatch) setTitle(headingMatch[1].trim());
    }
    if (fm.version) setVersion(fm.version);
    if (fm.status) setStatus(normalizeDocStatus(fm.status));
    if (fm.doc_type) setDocType(fm.doc_type);
    if (fm.family) setFamily(fm.family);
    if (fm.effective_date) setEffectiveDate(fm.effective_date);
    setPublicSlug(fm.slug ? String(fm.slug) : "");
    if (fm.supersedes) setSupersedes(String(fm.supersedes));
    else setSupersedes("");

    const {
      title: _t,
      version: _v,
      status: _s,
      doc_type: _d,
      family: _f,
      effective_date: _e,
      template: _tp,
      slug: _sl,
      ...rest
    } = fm;
    extraFmRef.current = rest;

    setMarkdown(content);
  }, []);

  useEffect(() => {
    if (!pageId) return;
    setLoading(true);
    setError(null);

    // ── New doc (blank, from a template, or from an uploaded .docx) ──
    if (isNewDoc) {
      try {
        const imported =
          location.state as Partial<ImportedDocLocationState> | null;
        if (imported?.importedMarkdown !== undefined) {
          setTitle(imported.importedTitle || "Untitled Document");
          if (imported.importedFamily) setFamily(imported.importedFamily);
          setMarkdown(imported.importedMarkdown);
          setDocType("pdfDocument");
          setDirty(true);
          setMessage(
            "converted from .docx — set the family/version, then save to open a PR",
          );
          window.history.replaceState({}, document.title);
        } else if (templateType && TEMPLATES[templateType]) {
          const raw = generateTemplateMarkdown(templateType);
          const { data: fm, content } = parseFrontMatter(raw);
          applyFrontMatter(fm, content);
        } else {
          setTitle("Untitled Document");
          setMarkdown("# Untitled Document\n\nStart writing...");
        }
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Failed to generate template",
        );
      }
      setLoading(false);
      return;
    }

    const applyServer = (raw: string, sha?: string) => {
      const { data: fm, content } = parseFrontMatter(raw);
      applyFrontMatter(fm, content);
      if (repoPath && !fm.version) setVersion(parseDocPath(repoPath).version);
      setFileSha(sha);
      serverSnapshotRef.current = raw;
      setDirty(false);

      const restored = (location.state as { restoredMarkdown?: string } | null)
        ?.restoredMarkdown;
      if (restored) {
        setMarkdown(restored);
        setDirty(true);
        setMessage(
          "reviewed draft loaded — set status to review, then review & publish",
        );
        window.history.replaceState({}, document.title);
      }
    };

    // ── Repo file (path-shaped id) — load from git ──
    if (repoPath) {
      api.git
        .file(repoPath)
        .then((data) => {
          applyServer(data.content, data.sha);
          if (loadDraft()) {
            setDraftPending(true);
            setHasDraft(true);
          }
        })
        .catch((e) => setError(formatApiError(e)))
        .finally(() => setLoading(false));
      return;
    }

    const draft = loadDraft();
    if (draft) {
      setMarkdown(draft.markdown);
      setTitle(draft.title);
      setVersion(draft.version);
      setStatus(draft.status);
      setDocType(draft.docType);
      setFamily(draft.family);
      setEffectiveDate(draft.effectiveDate);
      setPublicSlug(draft.publicSlug ?? "");
      setHasDraft(true);
      setDirty(true);
      setLoading(false);
      return;
    }

    // ── Notion page — fetch markdown export ──
    api.notion
      .markdown(pageId)
      .then((data) => {
        const { data: fm, content } = parseFrontMatter(data.markdown);
        applyFrontMatter(fm, content);
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [
    pageId,
    isNewDoc,
    templateType,
    repoPath,
    loadDraft,
    applyFrontMatter,
    location.state,
  ]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // ── "Revert to source" — discard draft and re-fetch from git/Notion ──
  const handleRevertToNotion = useCallback(() => {
    if (!pageId) return;
    clearDraft();
    setLoading(true);
    setError(null);

    const reload = repoPath
      ? api.git.file(repoPath).then((data) => parseFrontMatter(data.content))
      : api.notion
          .markdown(pageId)
          .then((data) => parseFrontMatter(data.markdown));

    reload
      .then(({ data: fm, content }) => applyFrontMatter(fm, content))
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Failed to load document"),
      )
      .finally(() => setLoading(false));
  }, [pageId, repoPath, clearDraft, applyFrontMatter]);

  const serializeEditable = useCallback(
    () =>
      JSON.stringify({
        markdown,
        title,
        version,
        status,
        docType,
        family,
        effectiveDate,
        publicSlug,
      }),
    [
      markdown,
      title,
      version,
      status,
      docType,
      family,
      effectiveDate,
      publicSlug,
    ],
  );

  // Capture the clean baseline once loading finishes; reset when a (re)load starts.
  useEffect(() => {
    if (loading) {
      cleanSnapshotRef.current = null;
      return;
    }
    if (cleanSnapshotRef.current === null) {
      cleanSnapshotRef.current = serializeEditable();
    }
  }, [loading, serializeEditable]);

  // ── Auto-save to localStorage (debounced 1s) ──
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Don't auto-save while still loading initial data
    if (!draftKey || loading) return;
    // Skip until a clean baseline exists, and when nothing changed since it —
    // this prevents a phantom dirty/draft ~1s after load with no user edits.
    if (
      cleanSnapshotRef.current === null ||
      serializeEditable() === cleanSnapshotRef.current
    )
      return;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      saveDraft({
        markdown,
        title,
        version,
        status,
        docType,
        family,
        effectiveDate,
        publicSlug,
      });
      setDirty(true);
    }, 1000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [
    draftKey,
    loading,
    markdown,
    title,
    version,
    status,
    docType,
    family,
    effectiveDate,
    publicSlug,
    saveDraft,
    serializeEditable,
  ]);

  useEffect(() => {
    if (title && !family) {
      setFamily(
        title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
      );
    }
  }, [title, family]);

  // Load sibling versions for suggestions (compare against prior published version).
  useEffect(() => {
    if (!family || !docType) return;
    let cancelled = false;
    fetchTree().then((docs) => {
      if (cancelled) return;
      const pathFamily = repoPath ? parseDocPath(repoPath).family : null;
      const familyKey =
        pathFamily ?? docs.find((d) => d.family.endsWith(`/${family}`))?.family;
      if (!familyKey) return;
      const siblings = docs
        .filter((d) => d.family === familyKey)
        .sort((a, b) => versionSortKey(a.version) - versionSortKey(b.version));
      setSiblingVersions(siblings);

      let nextBaseline = "";
      if (supersedes && siblings.some((s) => s.version === supersedes)) {
        nextBaseline = supersedes;
      } else {
        const idx = siblings.findIndex((s) => s.version === version);
        if (idx > 0) nextBaseline = siblings[idx - 1].version;
      }
      if (nextBaseline && nextBaseline !== version) {
        setBaselineVersion(nextBaseline);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [family, docType, version, supersedes, repoPath, fetchTree]);

  useEffect(() => {
    if (!baselineVersion || !family || !docType) {
      setBaselineContent(null);
      return;
    }
    let cancelled = false;
    const path = docPath(docType, family, baselineVersion);
    api.git
      .file(path)
      .then((data) => {
        if (!cancelled) {
          setBaselineContent(parseFrontMatter(data.content).content);
        }
      })
      .catch(() => {
        if (!cancelled) setBaselineContent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [baselineVersion, family, docType]);

  const openSuggestionsPage = useCallback(() => {
    if (!baselineVersion || !family || !docType) return;
    const familyKey = repoPath
      ? parseDocPath(repoPath).family
      : (siblingVersions[0]?.family ?? "");
    if (!familyKey) return;

    const from = docPath(docType, family, baselineVersion);
    const to = repoPath ?? docPath(docType, family, version);
    const state: SuggestionsLocationState = {
      draftBody: markdown,
      draftVersion: version,
      draftTitle: title,
      editPath: repoPath ?? to,
    };

    navigate(
      `/family/${encodeURIComponent(familyKey)}/suggestions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      { state },
    );
  }, [
    baselineVersion,
    family,
    docType,
    repoPath,
    siblingVersions,
    markdown,
    version,
    title,
    navigate,
  ]);

  // The Notion page id only applies when the editor was opened from Notion.
  const notionPageId =
    !isNewDoc && !repoPath
      ? pageId
      : (extraFmRef.current.notion_page_id as string | undefined);

  const buildFrontMatter = useCallback(
    (overrides: Partial<FrontMatter> = {}) => {
      // Always persist an explicit canonical slug: the typed override if any,
      // otherwise the one derived from the family. This keeps the public
      // /static/ URL stable across re-publishes instead of being re-derived.
      const resolvedSlug = websiteSlug(publicSlug, family);
      return stringifyFrontMatter(
        {
          ...extraFmRef.current,
          ...(notionPageId ? { notion_page_id: notionPageId } : {}),
          doc_type: docType,
          family,
          version,
          status,
          title,
          effective_date: effectiveDate,
          template: docType,
          ...(resolvedSlug ? { slug: resolvedSlug } : {}),
          ...overrides,
        },
        markdown,
      );
    },
    [
      notionPageId,
      docType,
      family,
      version,
      status,
      title,
      effectiveDate,
      publicSlug,
      markdown,
    ],
  );

  const handleSave = useCallback(async () => {
    if (!family.trim()) {
      addToast("set a family slug first", "error");
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const savedPath = docPath(docType, family, version);
      const content = buildFrontMatter();
      const commit = (sha?: string) =>
        api.git.commit({
          path: savedPath,
          content,
          message: `update: ${title} ${version}`,
          sha,
          action: "save",
          batch: batchId,
        });
      let result;
      try {
        result = await commit(fileSha);
      } catch (e) {
        // Stale sha (file changed in git since load, e.g. a reindex or another
        // save): refetch the current sha and retry once so the user isn't
        // forced to reload. Any other error propagates.
        if (e instanceof Error && /^API 409:/.test(e.message)) {
          const latest = await api.git
            .file(savedPath, prBranchRef.current ?? undefined)
            .catch(() => null);
          result = await commit(latest?.sha);
        } else {
          throw e;
        }
      }
      setFileSha(result.sha);
      if (result.pr?.branch) prBranchRef.current = result.pr.branch;
      serverSnapshotRef.current = buildFrontMatter();
      cleanSnapshotRef.current = serializeEditable();
      clearDraft();
      setDraftPending(false);
      setDirty(false);
      invalidateGit();
      addAuditEntry("save", "committed to git", family, version, actorEmail);
      if (result.pr?.url) {
        setMessage(`saved — opened PR #${result.pr.number}`);
        addToast(`saved — opened PR #${result.pr.number}`, "success");
      } else if (result.pr?.branch) {
        // Batched save: staged on the shared draft branch, PR opened later via
        // the floating PR button.
        stageDoc(savedPath);
        setMessage("saved — staged for PR");
        addToast("saved — staged for PR", "success");
      } else {
        setMessage("saved");
        addToast("saved to git", "success");
      }
      return true;
    } catch (e) {
      const detail = formatApiError(e);
      setMessage(`save failed: ${detail}`);
      addToast(detail, "error");
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    title,
    version,
    docType,
    family,
    buildFrontMatter,
    fileSha,
    clearDraft,
    invalidateGit,
    addAuditEntry,
    addToast,
    serializeEditable,
    batchId,
    stageDoc,
    actorEmail,
  ]);

  const handleGeneratePdf = useCallback(async () => {
    setGenerating("pdf");
    setMessage(null);
    try {
      const { response: res } = await api.convert.pdf({
        template: docType,
        title,
        version,
        content: markdown,
        metadata: {
          title,
          version,
          effectiveDate,
          status,
          ticker: extraFmRef.current.ticker ?? "",
        },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${family}-${version}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        setMessage("pdf downloaded");
      } else {
        setMessage("pdf unavailable on server");
        addToast("pdf unavailable", "error");
      }
    } catch {
      setMessage("pdf unavailable");
      addToast("pdf unavailable", "error");
    } finally {
      setGenerating(null);
    }
  }, [docType, family, version, markdown, title, effectiveDate, status]);

  const handleGoPublish = useCallback(async () => {
    if (!canPublish(status)) {
      addToast("set status to review first", "error");
      return;
    }
    // With PRs, publishing opens a PR in git (the save below) and the Sanity
    // publish happens after that PR is merged. So auto-save unsaved edits here
    // instead of forcing a separate "save to git" click first.
    if (dirty || !fileSha) {
      const ok = await handleSave();
      if (!ok) return;
    }
    const publishFamily = parseDocPath(
      docPath(docType, family, version),
    ).family;
    navigate(
      `/publish/${encodeURIComponent(publishFamily)}/${encodeURIComponent(version)}`,
      { state: { docType } },
    );
  }, [
    status,
    dirty,
    fileSha,
    docType,
    family,
    version,
    navigate,
    addToast,
    handleSave,
  ]);

  // ── Fork as new family ──
  const [showForkModal, setShowForkModal] = useState(false);
  const [showSplitPanel, setShowSplitPanel] = useState(false);
  const [forkSlug, setForkSlug] = useState("");
  const [forking, setForking] = useState(false);

  const openForkModal = useCallback(() => {
    setForkSlug(family ? `${family}-fork` : slugify(title));
    setShowForkModal(true);
  }, [family, title]);

  const handleFork = useCallback(async () => {
    const slug = slugify(forkSlug);
    if (!slug) return;
    setForking(true);
    setMessage(null);
    try {
      const targetPath = docPath(docType, slug, "v0.1");
      const targetFamily = parseDocPath(targetPath).family;

      const tree = await api.git.tree().catch(() => ({ docs: [] }));
      if (tree.docs.some((d) => d.family === targetFamily)) {
        throw new Error(`Family "${slug}" already exists in the repo.`);
      }

      // Fresh lineage: drop Notion linkage and supersedes, restart at v0.1.
      const content = stringifyFrontMatter(
        {
          doc_type: docType,
          family: slug,
          version: "v0.1",
          status: "draft",
          title,
          effective_date: new Date().toISOString().split("T")[0],
          template: docType,
        },
        markdown,
      );
      await api.git.commit({
        path: targetPath,
        content,
        message: `fork: ${family || title} → ${slug}`,
        action: "fork",
        batch: batchId,
      });
      stageDoc(targetPath);
      invalidateGit();

      addAuditEntry(
        "fork",
        `from ${family || title}`,
        slug,
        "v0.1",
        actorEmail,
      );
      addToast(`forked into ${slug}`, "success");
      setShowForkModal(false);
      navigate(`/doc/${encodeURIComponent(targetPath)}`);
    } catch (e) {
      const detail = e instanceof Error ? e.message : "Unknown error";
      setMessage(`fork failed: ${detail}`);
      addToast("fork failed", "error");
    } finally {
      setForking(false);
    }
  }, [
    forkSlug,
    docType,
    title,
    family,
    markdown,
    navigate,
    addAuditEntry,
    addToast,
    batchId,
    stageDoc,
    invalidateGit,
    actorEmail,
  ]);

  if (loading) {
    return (
      <>
        <TopBar title="loading..." />
        <div style={styles.loadingWrap}>
          <Spinner size={24} label="loading…" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar title="error" />
        <div style={styles.loadingWrap}>
          <p style={styles.errorText}>{error}</p>
          <Button onClick={() => navigate("/")}>dashboard</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar
        title={title || "Document"}
        actions={
          <div style={styles.topActions}>
            <Button onClick={() => navigate("/")}>dashboard</Button>
            {baselineContent && baselineVersion !== version && (
              <Button onClick={openSuggestionsPage}>open suggestions</Button>
            )}
            <Button onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? "hide preview" : "show preview"}
            </Button>
          </div>
        }
      />
      <div style={styles.mainLayout}>
        {/* Left: Metadata sidebar */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarSection}>
            <label style={styles.fieldLabel}>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              style={styles.fieldInput}
            />

            <label style={styles.fieldLabel}>Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              title="Drives the generated PDF cover and the publish target"
              style={styles.fieldInput}
            >
              {!DOC_TYPE_OPTIONS.some((o) => o.value === docType) && (
                <option value={docType}>{docType}</option>
              )}
              {DOC_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <label style={styles.fieldLabel}>Version</label>
            <input
              type="text"
              value={version}
              readOnly
              title="Versions are managed automatically"
              style={{
                ...styles.fieldInput,
                color: "var(--fg-3)",
                cursor: "not-allowed",
              }}
            />

            <label style={styles.fieldLabel}>Status</label>
            <div style={styles.statusRow}>
              {(
                ["draft", "review", "published", "superseded"] as DocStatus[]
              ).map((s) => {
                const allowed = canTransition(status, s);
                return (
                  <button
                    key={s}
                    onClick={allowed ? () => setStatus(s) : undefined}
                    disabled={!allowed}
                    style={{
                      ...styles.statusOption,
                      ...(!allowed ? styles.statusOptionDisabled : {}),
                      ...(status === s ? styles.statusOptionActive : {}),
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>

            <label style={styles.fieldLabel}>Effective Date</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              style={styles.fieldInput}
            />

            <label style={styles.fieldLabel}>Family</label>
            <input
              type="text"
              value={family}
              onChange={(e) => setFamily(e.target.value)}
              title="Internal family slug — the git folder this version lives in"
              style={styles.fieldInput}
            />

            <label style={styles.fieldLabel}>Public Slug</label>
            <input
              type="text"
              value={publicSlug}
              onChange={(e) => setPublicSlug(e.target.value)}
              placeholder={websiteSlug("", family) || "auto from family"}
              title="Public website URL slug (/static/<slug>). Leave blank to derive from the family."
              style={{ ...styles.fieldInput, borderBottom: "none" }}
            />
            {family && (
              <div style={styles.slugHint}>
                /static/{websiteSlug(publicSlug, family)}
              </div>
            )}

            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              style={{
                width: "100%",
                marginTop: "var(--space-2)",
                marginBottom: "var(--space-2)",
              }}
            >
              {saving ? "saving..." : "save to git"}
            </Button>
            <Button
              variant="primary"
              onClick={handleGoPublish}
              style={{ width: "100%", marginBottom: "var(--space-2)" }}
            >
              review & publish
            </Button>
            <div style={styles.actionLinks}>
              <button
                onClick={handleGeneratePdf}
                disabled={!!generating}
                style={styles.actionLink}
              >
                {generating === "pdf" ? "converting..." : "generate pdf"}
              </button>
              <button onClick={openForkModal} style={styles.actionLink}>
                fork
              </button>
              {markdown.trim() && (
                <button
                  onClick={() => setShowSplitPanel(true)}
                  style={styles.actionLink}
                >
                  split into snippets
                </button>
              )}
              {hasDraft && !draftPending && (
                <button onClick={handleRevertToNotion} style={styles.revertBtn}>
                  {repoPath ? "revert to git" : "revert to notion"}
                </button>
              )}
            </div>
            {draftPending && (
              <div style={styles.draftLine}>
                <span>local draft available</span>
                <button
                  type="button"
                  onClick={() => {
                    const d = loadDraft();
                    if (!d) return;
                    setMarkdown(d.markdown);
                    setTitle(d.title);
                    setVersion(d.version);
                    setStatus(d.status);
                    setDocType(d.docType);
                    setFamily(d.family);
                    setEffectiveDate(d.effectiveDate);
                    setPublicSlug(d.publicSlug ?? "");
                    setDraftPending(false);
                    setDirty(true);
                  }}
                  style={styles.draftBtn}
                >
                  restore
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearDraft();
                    setDraftPending(false);
                  }}
                  style={styles.draftBtn}
                >
                  discard
                </button>
              </div>
            )}
          </div>

          {message && <div style={styles.messageBox}>{message}</div>}
        </div>

        {/* Center: Markdown editor */}
        <div style={styles.editorPane}>
          <MarkdownToolbar
            setMarkdown={setMarkdown}
            textareaRef={textareaRef}
          />
          <textarea
            ref={textareaRef}
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            style={styles.textarea}
            placeholder="Start writing or edit imported content..."
            spellCheck={false}
          />
        </div>

        {/* Right: Preview */}
        {showPreview && (
          <div style={styles.previewPane}>
            <div style={styles.previewHeader}>
              <span>preview</span>
            </div>
            <style>{previewElementCss}</style>
            <div
              className="shelf-md-preview"
              style={styles.previewContent}
              dangerouslySetInnerHTML={{
                __html: renderMarkdownPreview(markdown),
              }}
            />
          </div>
        )}
      </div>

      {showSplitPanel && (
        <SnippetSplitPanel
          markdown={markdown}
          docType={docType}
          onClose={() => setShowSplitPanel(false)}
        />
      )}

      {/* ── Fork Modal ── */}
      {showForkModal && (
        <div style={bumpModalStyles.overlay}>
          <div style={bumpModalStyles.card}>
            <h3 style={bumpModalStyles.heading}>fork as new family</h3>
            <p style={bumpModalStyles.body}>
              copies current content into a new family at v0.1.
            </p>

            <label style={styles.fieldLabel}>New Family Slug</label>
            <input
              type="text"
              value={forkSlug}
              onChange={(e) => setForkSlug(e.target.value)}
              style={styles.fieldInput}
              autoFocus
            />

            <div style={bumpModalStyles.btnRow}>
              <Button
                onClick={() => setShowForkModal(false)}
                disabled={forking}
              >
                cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleFork}
                disabled={forking || !slugify(forkSlug)}
              >
                {forking ? "forking..." : "create fork"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
  },
  errorText: {
    color: "var(--comp-salmon)",
    fontSize: 13,
    marginBottom: "var(--space-4)",
  },
  topActions: {
    display: "flex",
    gap: "var(--space-2)",
    alignItems: "center",
  },
  mainLayout: {
    display: "flex",
    padding: "0 var(--page-pad-x)",
    height:
      "calc(100vh - var(--topnav-height) - var(--utilbar-height) - var(--pagehead-height) - var(--space-4))",
  },
  sidebar: {
    width: 240,
    minWidth: 240,
    borderRight: "1px solid var(--comp-rule)",
    background: "var(--comp-paper)",
    overflowY: "auto",
    padding: "var(--space-3) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  },
  sidebarSection: {
    marginBottom: "var(--space-3)",
    paddingBottom: "var(--space-3)",
  },
  fieldLabel: {
    display: "block",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "lowercase" as const,
    color: "var(--fg-3)",
    marginBottom: "var(--space-1)",
    marginTop: "var(--space-2)",
  },
  fieldInput: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    borderRadius: 0,
    padding: "4px 0",
    height: "var(--control-h)",
    fontSize: 12,
    fontFamily: "var(--font-main)",
    background: "transparent",
    color: "var(--fg-1)",
    outline: "none",
  },
  fieldSelect: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    borderRadius: 0,
    padding: "4px 0",
    height: "var(--control-h)",
    fontSize: 12,
    fontFamily: "var(--font-main)",
    background: "transparent",
    color: "var(--fg-1)",
    outline: "none",
  },
  slugHint: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    color: "var(--fg-3)",
    marginTop: "var(--space-1)",
    wordBreak: "break-all" as const,
  },
  statusRow: {
    display: "flex",
    flexWrap: "nowrap" as const,
    gap: 4,
    marginTop: "var(--space-1)",
  },
  statusOption: {
    padding: "2px 3px",
    border: "none",
    borderBottom: "2px solid transparent",
    borderRadius: 0,
    background: "transparent",
    cursor: "pointer",
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    fontWeight: 500,
    color: "var(--fg-3)",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  },
  statusOptionActive: {
    borderBottom: "2px solid var(--fg-1)",
    color: "var(--fg-1)",
    fontWeight: 600,
  },
  statusOptionDisabled: {
    color: "var(--fg-mute)",
    cursor: "not-allowed",
  },
  actionLinks: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: 4,
    marginLeft: 0,
    paddingLeft: 0,
  },
  actionLink: {
    fontSize: 11,
    color: "var(--fg-2)",
    textAlign: "left" as const,
    padding: "2px 0",
    marginLeft: 0,
    paddingLeft: 0,
    textIndent: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--font-mono)",
  },
  draftLine: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    marginTop: 8,
    fontSize: 11,
    color: "var(--fg-2)",
  },
  draftBtn: {
    fontSize: 11,
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "var(--fg-1)",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  messageBox: {
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--fg-3)",
    lineHeight: 1.5,
  },
  revertBtn: {
    fontSize: 11,
    color: "var(--comp-salmon)",
    textAlign: "left" as const,
    padding: "2px 0",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--font-mono)",
  },
  editorPane: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    background: "var(--comp-paper)",
  },
  textarea: {
    width: "100%",
    maxWidth: 880,
    margin: "0 auto",
    flex: 1,
    padding: "24px 32px",
    border: "none",
    outline: "none",
    resize: "none" as const,
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    lineHeight: 1.75,
    color: "var(--fg-1)",
    background: "var(--comp-paper)",
  },
  previewPane: {
    width: "40%",
    minWidth: 300,
    borderLeft: "1px solid var(--comp-rule)",
    background: "var(--comp-paper)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
  },
  previewHeader: {
    height: 37,
    boxSizing: "border-box" as const,
    padding: "0 8px",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "lowercase" as const,
    color: "var(--fg-3)",
    borderBottom: "1px solid var(--comp-rule)",
    background: "var(--comp-paper)",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  previewContent: {
    maxWidth: 720,
    margin: "0 auto",
    padding: "24px 32px",
    fontSize: 13,
    lineHeight: 1.75,
    color: "var(--fg-1)",
    fontFamily: "var(--font-main)",
    overflowY: "auto",
    flex: 1,
  },
};

const bumpModalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "var(--overlay-deep)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  card: {
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-4)",
    width: 360,
    maxWidth: "90vw",
  },
  heading: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-1)",
    marginBottom: "var(--space-4)",
  },
  body: {
    fontSize: 13,
    color: "var(--fg-2)",
    lineHeight: 1.6,
    margin: "0 0 var(--space-4)",
  },
  btnRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "var(--space-2)",
    marginTop: "var(--space-4)",
  },
};
